import { useNavigationSync } from '@sudoplatform-private/admin-web-ui'
import {
  getPathAfterRoot,
  type DynamicBreadcrumbsHandlerContext,
  type MountProps,
} from 'admin-web-utils'
import { Spin } from 'antd'
import { kebabCase } from 'lodash'
import React, { useMemo, useRef, useState } from 'react'
import { useLocation, useOutletContext } from 'react-router'
import { useMount } from 'react-use'
import styled from 'styled-components'
import { useAppContext } from '../../containers/App'
import { useConsoleContext } from '../../containers/Console'
import { useProjectContext } from '../../containers/Project'
import { useFederatedModule } from '../../hooks/use-federated-module'

const Container = styled.div`
  display: inherit;
  flex-direction: inherit;
  align-items: inherit;
  width: 100%;
  height: 100%;
`

const StyledSpin = styled(Spin)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`

function extractRootPath(initialPath: string, scope: string): string {
  const pathParts = initialPath.split('/')
  const subAppRootIndex = pathParts.indexOf(kebabCase(scope))
  const rootPathParts = pathParts.slice(0, subAppRootIndex + 1)

  return rootPathParts.join('/')
}

interface Props {
  remoteUrl: string
  scope: string
  module: string
}

export const SubAppContainer: React.FC<Props> = ({
  remoteUrl,
  scope,
  module,
}) => {
  const ref = useRef(null)
  const { pathname, search } = useLocation()
  const [rootPath, setRootPath] = useState('')
  const [mountProps, setMountProps] = useState<MountProps>()
  const [subAppPath, setSubAppPath] = useState('')
  const { featureFlags } = useAppContext()
  const { getAccessToken } = useConsoleContext()
  const { me, currentProject, projectClient } = useProjectContext()
  const { addDynamicBreadcrumbs } =
    useOutletContext<DynamicBreadcrumbsHandlerContext>()
  const [SubAppModule, errorLoading] = useFederatedModule(
    remoteUrl,
    scope,
    module,
  )

  /**
   * Initializes rootPath and mountProps.
   * NOTE: because mountProps is initialized only once, the sub app will not
   *  get any updated references within it. This means that onSubAppNavigate
   *  cannot depend on any variables that are expected to be updated at runtime.
   */
  useMount(() => {
    const rootPath = extractRootPath(pathname, scope)
    const initialPath = getPathAfterRoot(pathname + search, rootPath)
    setRootPath(rootPath)
    setMountProps({
      me,
      projectId: currentProject.id,
      projectClient,
      initialPath,
      featureFlags,
      getAccessToken,
      onSubAppNavigate: (nextPathname: string) => setSubAppPath(nextPathname),
      addDynamicBreadcrumbs,
    })
  })

  /**
   * Mounts the Sub App and returns a memoized copy of its onContainerNavigate callback
   */
  const onContainerNavigate = useMemo(() => {
    if (!SubAppModule || errorLoading || !mountProps) return

    const { onContainerNavigate } = SubAppModule.mount(ref.current, mountProps)

    return onContainerNavigate
  }, [SubAppModule, errorLoading, mountProps])

  useNavigationSync(subAppPath, onContainerNavigate, rootPath)

  return (
    <>
      {!SubAppModule && !errorLoading && <StyledSpin />}
      <Container ref={ref} />
    </>
  )
}
